import React, { useState, FormEventHandler, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PrimaryButton from "@/Components/Global/PrimaryButton";
import TextInput from "../Global/TextInput";
import InputError from "@/Components/Global/InputError";
import TextAreaInput from "../Global/TextArea";

declare global {
    interface Window {
        grecaptcha: any;
    }
}

const ContactForm: React.FC = () => {
    const [data, setData] = useState({
        name: "",
        email: "",
        message: "",
        recaptcha_token: "",
    });

    const [processing, setProcessing] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [showErrors, setShowErrors] = useState(false);

    useEffect(() => {
        if (errors.name || errors.email || errors.message) {
            setShowErrors(true);
        }
    }, [errors]);

    const handleSubmit: FormEventHandler = async (e) => {
        e.preventDefault();
        console.log("Form submitted!");

        setShowErrors(true);
        setProcessing(true);

        if (!window.grecaptcha || !window.grecaptcha.execute) {
            console.error("reCAPTCHA is not loaded.");
            toast.error("reCAPTCHA failed to load. Please refresh the page.");
            setProcessing(false);
            return;
        }

        try {
            await new Promise((resolve) => window.grecaptcha.ready(resolve));

            const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
            if (!recaptchaSiteKey) {
                throw new Error("Missing reCAPTCHA site key.");
            }

            const token = await window.grecaptcha.execute(recaptchaSiteKey, {
                action: "submit",
            });

            const finalData = { ...data, recaptcha_token: token };

            await axios.post("/guest-contact/send", finalData);

            setData({ name: "", email: "", message: "", recaptcha_token: "" });
            setShowErrors(false);
            toast.success("Message sent successfully!");
        } catch (error) {
            console.error("Caught error in handleSubmit:", error);
            if (axios.isAxiosError(error) && error.response?.data.errors) {
                setErrors(error.response.data.errors);
            } else {
                toast.error("Something went wrong. Please try again.");
            }
        } finally {
            setProcessing(false);
        }
    };

    const handleChange = (field: keyof typeof data, value: string) => {
        setData((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <div className="max-w-[500px] m-auto px-10">
            <ToastContainer />
            <form onSubmit={handleSubmit} className="w-full mx-auto">
                <div className="mb-4">
                    <TextInput
                        id="name"
                        name="name"
                        value={data.name}
                        className="mt-1 block w-full dark:bg-secondary-dark dark:text-text-dark text-sm md:text-base"
                        placeholder="Name"
                        onChange={(e) => handleChange("name", e.target.value)}
                        required
                    />
                    {showErrors && errors.name && (
                        <InputError message={errors.name} className="mt-2" />
                    )}
                </div>
                <div className="mb-4">
                    <TextInput
                        id="email"
                        type="email"
                        name="email"
                        value={data.email}
                        className="mt-1 block w-full dark:bg-secondary-dark dark:text-text-dark text-sm md:text-base"
                        autoComplete="email"
                        placeholder="Email"
                        onChange={(e) => handleChange("email", e.target.value)}
                        required
                    />
                    {showErrors && errors.email && (
                        <InputError message={errors.email} className="mt-2" />
                    )}
                </div>
                <div className="mb-6">
                    <TextAreaInput
                        id="message"
                        name="message"
                        value={data.message}
                        className="mt-1 p-3 w-full h-32 dark:bg-secondary-dark dark:text-text-dark text-sm md:text-base"
                        placeholder="Message"
                        onChange={(e) =>
                            handleChange("message", e.target.value)
                        }
                        required
                    />
                    {showErrors && errors.message && (
                        <InputError message={errors.message} className="mt-2" />
                    )}
                </div>
                <div className="flex justify-end">
                    <div className="w-2/4">
                        <PrimaryButton
                            type="submit"
                            className="text-white text-[13px] py-1 md:py-3 md:text-[18px] w-full"
                            disabled={processing}
                        >
                            {processing ? "Sending..." : "SEND"}
                        </PrimaryButton>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ContactForm;
